import { TaskTypeStatus } from '@econtractor/types';
import { useTranslation } from 'react-i18next';
import SolarIcon from 'components/icons/SolarIcon';

interface TaskStatusBadgeProps {
    status: number;
    dateEnd?: number | null;
    /** Soft-delete: візуально показує «Видалена» замість реального статусу */
    taskDeleted?: number | boolean | null;
}

export type TaskStatusBadgeTone =
    | 'new'
    | 'inwork'
    | 'completed'
    | 'accepted'
    | 'cancelled'
    | 'reject'
    | 'overdue'
    | 'deleted';

const statusTone = (status: number): TaskStatusBadgeTone => {
    switch (status) {
        case TaskTypeStatus.NEW:
            return 'new';
        case TaskTypeStatus.INWORK:
            return 'inwork';
        case TaskTypeStatus.COMPLETED:
            return 'completed';
        case TaskTypeStatus.ACCEPTED:
            return 'accepted';
        case TaskTypeStatus.CANCELLED:
            return 'cancelled';
        case TaskTypeStatus.REJECT:
            return 'reject';
        default:
            return 'new';
    }
};

/** Той самий тон, що й у бейджа статусу (включно з простроченою і видаленою). */
export const getTaskStatusBadgeTone = (
    status: number,
    dateEnd?: number | null,
    taskDeleted?: number | boolean | null,
    now = Date.now(),
): TaskStatusBadgeTone => {
    const isDeleted = Boolean(Number(taskDeleted));
    if (isDeleted) return 'deleted';

    const isOpen =
        status !== TaskTypeStatus.COMPLETED &&
        status !== TaskTypeStatus.ACCEPTED &&
        status !== TaskTypeStatus.CANCELLED &&
        status !== TaskTypeStatus.REJECT;
    const overdue = isOpen && Number(dateEnd || 0) > 0 && Number(dateEnd) < now;
    return overdue ? 'overdue' : statusTone(status);
};

const toneMeta: Record<TaskStatusBadgeTone, { icon: string }> = {
    new: { icon: 'bi-circle' },
    inwork: { icon: 'bi-play-fill' },
    completed: { icon: 'bi-check2-all' },
    accepted: { icon: 'bi-hand-thumbs-up' },
    cancelled: { icon: 'bi-x-circle' },
    reject: { icon: 'bi-slash-circle' },
    overdue: { icon: 'bi-exclamation-circle' },
    deleted: { icon: 'bi-trash3' },
};

const TaskStatusBadge = ({
    status,
    dateEnd = null,
    taskDeleted = false,
}: TaskStatusBadgeProps) => {
    const { t } = useTranslation(['Tasks']);
    const now = Date.now();
    const isDeleted = Boolean(Number(taskDeleted));
    const tone = getTaskStatusBadgeTone(status, dateEnd, taskDeleted, now);
    const overdue = tone === 'overdue';

    const label = isDeleted
        ? t('status.deleted')
        : overdue
          ? t('status.overdue')
          : t(`status.${status}`, { defaultValue: String(status) });

    return (
        <span className={`badge task-status-badge task-status-badge--${tone}`}>
            <SolarIcon name={toneMeta[tone].icon} className="me-1" />
            {label}
        </span>
    );
};

export default TaskStatusBadge;
